import { t } from 'i18next';
import * as yup from 'yup';
import { TestContext } from 'yup';

import { getEmptyErrorMessage } from 'core/yup';
import { CostValidityEntry } from 'quotes/enums';
import {
  ICostSourceFormValue,
  ISelectedSpaFormValue,
  TVendorQuoteCostFormValue,
} from 'quotes/types';

import { isCostValidityRangeValid, parseCostValidityDate, TCostValidityDate } from './costValidity';
import {
  getFormCostValidityEntry,
  getVendorQuoteValidSince,
  isOneTimeSpaFormCost,
  TOneTimeSpaRow,
} from './oneTimeSpa';

type TFormValues = Record<string, unknown>;

// The fields sit in costSource; the rules read the whole form.
const getFormValues = (context: TestContext): TFormValues | undefined =>
  (context as TestContext & { from?: { value: TFormValues }[] }).from?.[1]?.value;

/**
 * The cost's validity, for every form hosting the cost section. Both rules apply only where the
 * user gives the dates (getCostValidityEntry), as the fields and the payload decide it, so hidden
 * leftover dates never block a save: Valid until is required only on a one-time SPA line, and it
 * is never before Valid since, the typed one or the vendor quote's.
 */
export const getCostValiditySchemaFields = (
  isAlternateVersionEnabled: boolean,
  getIsShipAndDebitVendor: (formValues: TFormValues) => boolean,
  savedLine?: TOneTimeSpaRow | null
) => {
  const getEntry = (formValues?: TFormValues): CostValidityEntry =>
    formValues
      ? getFormCostValidityEntry(
          {
            costSource: formValues.costSource as ICostSourceFormValue | null,
            isCostingCustom: !!formValues.isCostingCustom,
            selectedSpa: formValues.selectedSpa as ISelectedSpaFormValue | null,
            vendorQuoteCost: formValues.vendorQuoteCost as TVendorQuoteCostFormValue | null,
          },
          isAlternateVersionEnabled,
          savedLine
        )
      : CostValidityEntry.NONE;

  const isValidUntilRequired = (formValues?: TFormValues): boolean =>
    CostValidityEntry.NONE !== getEntry(formValues) &&
    isOneTimeSpaFormCost({
      isShipAndDebitVendor: getIsShipAndDebitVendor(formValues),
      purchaseCost: formValues.purchaseCost as number,
      replacementCost: formValues.replacementCost as number,
    });

  const getValidSince = (
    formValues: TFormValues | undefined,
    validSince: TCostValidityDate
  ): TCostValidityDate => {
    switch (getEntry(formValues)) {
      case CostValidityEntry.TYPED:
        return validSince;
      case CostValidityEntry.VENDOR_QUOTE_VALID_UNTIL:
        return parseCostValidityDate(
          getVendorQuoteValidSince(
            formValues.vendorQuoteCost as TVendorQuoteCostFormValue | null,
            savedLine
          )
        );
      default:
        return null;
    }
  };

  return {
    validSince: yup.mixed().label(t('labelValidSince')).nullable(),
    validUntil: yup
      .mixed()
      .label(t('labelValidUntil'))
      .nullable()
      .test('requiredCostValidUntil', getEmptyErrorMessage(t('labelValidUntil')), function (value) {
        return !!value || !isValidUntilRequired(getFormValues(this));
      })
      .test(
        'costValidUntilNotBeforeValidSince',
        t('errorCostValidUntilBeforeValidSince'),
        function (value) {
          return isCostValidityRangeValid(
            getValidSince(getFormValues(this), this.parent?.validSince as TCostValidityDate),
            value
          );
        }
      ),
  };
};
